import React, { useEffect, useState, memo, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Package, GraduationCap, Bus, Coffee, ChevronDown, X, Heart, Shield, BookOpen, Users, Globe, Camera, Star, Award, Crown } from 'lucide-react';
// --- Atoms ---
const OrnateDivider = ({
  className = ''
}: {
  className?: string;
}) => <div className={`flex items-center justify-center gap-3 ${className}`} data-id="element-8275" data-mp="3k6p97ol6depb">
    <span className="h-px w-12 sm:w-20 bg-gradient-to-r from-transparent to-[#D4AF37]/60" data-id="element-8276" data-mp="3g3swufm8aett" />
    <svg width="28" height="14" viewBox="0 0 28 14" fill="none" data-id="element-8277" data-mp="3b4pqmruii2k9">
      <path d="M14 0 L17 4 L24 5 L19 9 L20 14 L14 11 L8 14 L9 9 L4 5 L11 4 Z" fill="#D4AF37" data-id="element-8278" data-mp="3b7bzcut52o42" />
    </svg>
    <span className="h-px w-12 sm:w-20 bg-gradient-to-l from-transparent to-[#D4AF37]/60" data-id="element-8279" data-mp="19dmd9ut8m9w0" />
  </div>;
const CornerOrnaments = () => <>
    <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#D4AF37]/40 opacity-50" data-id="element-8280" data-mp="14g2zsx4d6154" />
    <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-[#D4AF37]/40 opacity-50" data-id="element-8281" data-mp="15o0amjg7kxvq" />
    <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-[#D4AF37]/40 opacity-50" data-id="element-8282" data-mp="19shsue6x734e" />
    <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#D4AF37]/40 opacity-50" data-id="element-8283" data-mp="vti476jeqo88" />
  </>;
const SectionHeader = ({
  title,
  subtitle
}: {
  title: string;
  subtitle?: string;
}) => <div className="text-center mb-16" data-id="element-8284" data-mp="1oifasce9awl5">
    <OrnateDivider className="mb-6" data-id="element-8285" data-mp="1tfffojzddtao" />
    <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-8286" data-mp="1tecr4ipis2xx">
      {title}
    </h2>
    {subtitle && <p className="text-[#FDFBF7]/60 text-sm max-w-2xl mx-auto" data-id="element-8287" data-mp="1eo5falu0pi67">{subtitle}</p>}
  </div>;
// --- Modals ---
const BuyChaaModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [amount, setAmount] = useState<string>('21');
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setIsSuccess(false);
      setIsSubmitting(false);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setTimeout(onClose, 2500);
    }, 1500);
  };
  return <AnimatePresence data-id="element-8288" data-mp="1td88as3awzi1">
      {isOpen && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-8289" data-mp="1oedrfvow9l0k">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-[#0B0F19]/90 backdrop-blur-sm" data-id="element-8290" data-mp="1kcabouv6zu9h" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-md bg-[#131B2B] border border-[#D4AF37]/30 p-8 shadow-2xl" data-id="element-8291" data-mp="3vyudkt9qq6ou">
            <CornerOrnaments data-id="element-8292" data-mp="3hapv9qelnx6l" />
            <button onClick={onClose} className="absolute top-4 right-4 text-[#FDFBF7]/40 hover:text-[#D4AF37] transition-colors" data-id="element-8293" data-mp="3hahcxizpjzo0">
              <X className="w-5 h-5" data-id="element-8294" data-mp="2ombtr0oayiqa" />
            </button>

            {isSuccess ? <div className="text-center py-8" data-id="element-8295" data-mp="2tl58sxj8mhsk">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/30" data-id="element-8296" data-mp="2tju50xpsla1g">
                  <Coffee className="w-8 h-8 text-[#D4AF37]" data-id="element-8297" data-mp="v3bj2gkkxogo" />
                </div>
                <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-8298" data-mp="v18kv999wp0v">
                  Thank You
                </h3>
                <p className="text-[#FDFBF7]/60 text-sm" data-id="element-8299" data-mp="14418n2j8yily">
                  Your support means the world to our team.
                </p>
              </div> : <form onSubmit={handleSubmit} data-id="element-8300" data-mp="1iv0fo94foz3f">
                <div className="text-center mb-8" data-id="element-8301" data-mp="1dum6uov0dl4c">
                  <Coffee className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" data-id="element-8302" data-mp="1dvx92k8t2ktc" />
                  <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-8303" data-mp="1rt2cech4x5kw">
                    Buy Chaa for the Team
                  </h3>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-8304" data-mp="3tkz00kgku5w1">
                    Support the artists, guides, and builders bringing the
                    Lahore Treasury to life.
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4" data-id="element-8305" data-mp="3fex2lsfmbmbp">
                  {['11', '21', '51'].map(val => <button key={val} type="button" onClick={() => {
              setAmount(val);
              setCustomAmount('');
            }} className={`py-3 border text-sm transition-colors ${amount === val ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-[#D4AF37]/20 text-[#FDFBF7]/60 hover:border-[#D4AF37]/50'}`} data-id="element-8306" data-mp="3jfz28adg60e7">
                      ${val}
                    </button>)}
                </div>
                <div className="mb-6" data-id="element-8307" data-mp="2rpcekv4lxyvk">
                  <input type="number" placeholder="Custom Amount ($)" value={customAmount} onChange={e => {
              setCustomAmount(e.target.value);
              setAmount('custom');
            }} className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors" data-id="element-8308" data-mp="2rqe2i06q2fup" />
                </div>
                <div className="mb-8" data-id="element-8309" data-mp="el8rgpm2yo5i">
                  <textarea placeholder="Optional message to the team..." value={message} onChange={e => setMessage(e.target.value)} className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors h-24 resize-none" data-id="element-8310" data-mp="9kl0g3xuq7ba" />
                </div>
                <button type="submit" disabled={isSubmitting} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] font-bold tracking-[0.2em] uppercase text-sm hover:bg-[#E5C158] transition-colors disabled:opacity-50" data-id="element-8311" data-mp="shw2fwavbeuf">
                  {isSubmitting ? 'Processing...' : `Support with $${amount === 'custom' ? customAmount || '0' : amount}`}
                </button>
              </form>}
          </motion.div>
        </div>}
    </AnimatePresence>;
};
const SponsorInquiryModal = ({
  isOpen,
  onClose,
  defaultType = ''
}: {
  isOpen: boolean;
  onClose: () => void;
  defaultType?: string;
}) => {
  const [type, setType] = useState(defaultType);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setType(defaultType);
      setIsSuccess(false);
      setIsSubmitting(false);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, defaultType]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setTimeout(onClose, 3000);
    }, 1500);
  };
  return <AnimatePresence data-id="element-8312" data-mp="3gim2alj5qckl">
      {isOpen && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-8313" data-mp="3ugu2d2bhptzz">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-[#0B0F19]/90 backdrop-blur-sm" data-id="element-8314" data-mp="3phh8vyq45oof" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-lg bg-[#131B2B] border border-[#D4AF37]/30 p-8 shadow-2xl max-h-[90vh] overflow-y-auto scrollbar-hide" data-id="element-8315" data-mp="3bihudjwbeq5z">
            <CornerOrnaments data-id="element-8316" data-mp="ybi6zjegweob" />
            <button onClick={onClose} className="absolute top-4 right-4 text-[#FDFBF7]/40 hover:text-[#D4AF37] transition-colors" data-id="element-8317" data-mp="yctbxmjnfsh0">
              <X className="w-5 h-5" data-id="element-8318" data-mp="olgiwrm46w9e" />
            </button>

            {isSuccess ? <div className="text-center py-12" data-id="element-8319" data-mp="1cbszsrzc83c4">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/30" data-id="element-8320" data-mp="1h9m3ned64l98">
                  <Shield className="w-8 h-8 text-[#D4AF37]" data-id="element-8321" data-mp="1391kui796pfq" />
                </div>
                <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-8322" data-mp="1884r25yyz1pa">
                  Inquiry Received
                </h3>
                <p className="text-[#FDFBF7]/60 text-sm" data-id="element-8323" data-mp="3nyh9p7mww42q">
                  A member of the Lahore Treasury team will contact you shortly.
                </p>
              </div> : <form onSubmit={handleSubmit} data-id="element-8324" data-mp="21ig1meadixb1">
                <div className="text-center mb-8" data-id="element-8325" data-mp="25j9od4c2y62c">
                  <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-8326" data-mp="25izx7dfatsv2">
                    Become a Patron
                  </h3>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-8327" data-mp="1rqa6wwsqm1s5">
                    Help open the Lahore Court to the next generation.
                  </p>
                </div>

                <div className="space-y-4 mb-8" data-id="element-8328" data-mp="1hsm3lih2flyd">
                  <input required type="text" placeholder="Full Name" className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors" data-id="element-8329" data-mp="1mtjjtpiim5x8" />
                  <input required type="email" placeholder="Email Address" className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors" data-id="element-8330" data-mp="338bc82fnvd6j" />

                  <select value={type} onChange={e => setType(e.target.value)} required className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors appearance-none" data-id="element-8331" data-mp="3dcrsgwx1ywav">
                    <option value="" disabled data-id="element-8332" data-mp="3w27s7i0wykq8">
                      Select Sponsorship Type
                    </option>
                    <option value="Student Access" data-id="element-8333" data-mp="dv1xhedpihlm">
                      Student Access ($25+)
                    </option>
                    <option value="School Bus" data-id="element-8334" data-mp="9v9jun9ghl9e">School Bus ($1,500+)</option>
                    <option value="Collection" data-id="element-8335" data-mp="2fohqghx9o8jr">
                      Collection Sponsor ($5,000+)
                    </option>
                    <option value="Treasury Night" data-id="element-8336" data-mp="2bnny9ccsk7l2">
                      Treasury Night Sponsor
                    </option>
                    <option value="Commemorative Coin" data-id="element-8337" data-mp="jkbmsuhemzqf">
                      Commemorative Coin Sponsor
                    </option>
                    <option value="Founding Partner" data-id="element-8338" data-mp="oj4wscxhg8lu">
                      Founding Partner ($10,000+)
                    </option>
                    <option value="Court Packet" data-id="element-8339" data-mp="tjsoz1xg6vhn">Buy Court Packets</option>
                    <option value="Other" data-id="element-8340" data-mp="xj1flxx6yd8z">Other Contribution</option>
                  </select>

                  <select required className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors appearance-none" data-id="element-8341" data-mp="12i4et1qhpp91">
                    <option value="" disabled data-id="element-8342" data-mp="1h828fx76v75m">
                      Budget Range
                    </option>
                    <option value="Under $500" data-id="element-8343" data-mp="1l9dw3x6i5ib6">Under $500</option>
                    <option value="$500 - $2,500" data-id="element-8344" data-mp="1l7krsojd3a6c">$500 - $2,500</option>
                    <option value="$2,500 - $10,000" data-id="element-8345" data-mp="35shiwhvnthqs">$2,500 - $10,000</option>
                    <option value="$10,000+" data-id="element-8346" data-mp="31put0miju6w8">$10,000+</option>
                  </select>

                  <textarea placeholder="Message or specific requests..." className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors h-24 resize-none" data-id="element-8347" data-mp="2wqhxzehixtik" />
                </div>

                <button type="submit" disabled={isSubmitting} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] font-bold tracking-[0.2em] uppercase text-sm hover:bg-[#E5C158] transition-colors disabled:opacity-50" data-id="element-8348" data-mp="3khnfrz70yvvi">
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                </button>
              </form>}
          </motion.div>
        </div>}
    </AnimatePresence>;
};
// --- Main Page Component ---
export const SupportTheCourtPage = () => {
  const [isChaaModalOpen, setIsChaaModalOpen] = useState(false);
  const [isSponsorModalOpen, setIsSponsorModalOpen] = useState(false);
  const [sponsorModalType, setSponsorModalType] = useState('');
  const openSponsorModal = (type: string) => {
    setSponsorModalType(type);
    setIsSponsorModalOpen(true);
  };
  // Impact Calculator State
  const [impactAmount, setImpactAmount] = useState<number>(25);
  const [customImpact, setCustomImpact] = useState<string>('');
  const calculateImpact = (amount: number) => {
    const students = Math.floor(amount / 25);
    const packets = students;
    const buses = Math.floor(amount / 2000);
    return {
      students,
      packets,
      buses
    };
  };
  const currentImpact = calculateImpact(impactAmount);
  return <div className="min-h-screen w-full bg-[#0B0F19] text-[#FDFBF7] font-sans selection:bg-[#D4AF37] selection:text-[#0B0F19]" data-id="element-8349" data-mp="tg3it0aa3t2m">
      {/* Top Bar */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0B0F19]/90 border-b border-[#D4AF37]/20" data-id="element-8350" data-mp="2iy0dmxvyerb0">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between" data-id="element-8351" data-mp="2e08oucqlvlb1">
          <Link to="/treasure" className="text-[#D4AF37]/70 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase flex items-center gap-1 transition-colors" data-id="element-8352" data-mp="2rrdgosnqf9at">
            <ChevronLeft className="w-4 h-4" data-id="element-8353" data-mp="1zzegfu3k5kcd" /> Treasury Home
          </Link>
          <div className="font-serif text-xs sm:text-sm text-[#D4AF37] tracking-[0.25em] uppercase text-center" data-id="element-8354" data-mp="201ip0pcej7iz">
            Support the Court
          </div>
          <div className="w-24" data-id="element-8355" data-mp="2915h2kj8r0zd" />
        </div>
      </header>

      {/* 1. Hero Section */}
      <section className="relative pt-24 pb-32 px-4 overflow-hidden" data-id="element-8356" data-mp="240hse5ihiw8b">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.08),_transparent_60%)]" data-id="element-8357" data-mp="3kezu0smrhsav" />
        <div className="max-w-5xl mx-auto relative z-10 text-center" data-id="element-8358" data-mp="3ffekjo9yu1jw">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-8359" data-mp="2d3a5zq8vb3e3">
            <p className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase mb-6" data-id="element-8360" data-mp="2m1vmlfus48c8">
              Lahore Treasury
            </p>
            <h1 className="font-serif text-5xl sm:text-7xl text-[#FDFBF7] mb-8 leading-tight" data-id="element-8361" data-mp="1ud28porun75t">
              Help Open the <br className="hidden sm:block" data-id="element-8362" data-mp="1ydch183469nu" />
              <span className="text-[#D4AF37]" data-id="element-8363" data-mp="1yc1ch61ugxvo">Lahore Court</span>
            </h1>
            <p className="text-[#FDFBF7]/70 text-lg max-w-2xl mx-auto mb-12 leading-relaxed" data-id="element-8364" data-mp="23bnlf3an3ryo">
              Lahore Treasury is more than a collectible platform. It is a
              cultural experience designed to bring the world of Maharaja Ranjit
              Singh’s Lahore Court to families, students, schools, and the next
              generation.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20" data-id="element-8365" data-mp="18327inhr49dn">
              <button onClick={() => openSponsorModal('Student Access')} className="w-full sm:w-auto px-8 py-4 bg-[#D4AF37] text-[#0B0F19] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#E5C158] transition-colors" data-id="element-8366" data-mp="13621udotd8n2">
                Sponsor a Student
              </button>
              <button onClick={() => openSponsorModal('School Bus')} className="w-full sm:w-auto px-8 py-4 border border-[#D4AF37]/40 text-[#D4AF37] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#D4AF37]/10 transition-colors" data-id="element-8367" data-mp="p6u2nksni0iv">
                Sponsor a Bus
              </button>
              <button onClick={() => openSponsorModal('Founding Partner')} className="w-full sm:w-auto px-8 py-4 border border-[#FDFBF7]/20 text-[#FDFBF7] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#FDFBF7]/10 transition-colors" data-id="element-8368" data-mp="6erp4ibb30cu">
                Become a Patron
              </button>
            </div>

            {/* Hero Visual: Elegant Sealed Packet */}
            <div className="relative w-48 h-64 mx-auto group" data-id="element-8369" data-mp="1gc7op0yq61kz">
              <div className="absolute inset-0 bg-gradient-to-br from-[#131B2B] to-[#0B0F19] border border-[#D4AF37]/40 shadow-[0_0_40px_rgba(212,175,55,0.1)] group-hover:border-[#D4AF37] group-hover:shadow-[0_0_60px_rgba(212,175,55,0.2)] transition-all duration-700 flex items-center justify-center" data-id="element-8370" data-mp="1gdr993reyysk">
                <CornerOrnaments data-id="element-8371" data-mp="1beel8fot3loe" />
                <div className="w-12 h-12 rounded-full bg-[#8B0000] border-2 border-[#D4AF37] flex items-center justify-center shadow-lg relative z-10" data-id="element-8372" data-mp="wo78mglhcwvm">
                  <Crown className="w-5 h-5 text-[#D4AF37]" data-id="element-8373" data-mp="11kpddggt8v7y" />
                </div>
                <div className="absolute top-1/2 left-0 w-full h-px bg-[#D4AF37]/30 -translate-y-1/2" data-id="element-8374" data-mp="nniwjrfnd6ld" />
                <div className="absolute left-1/2 top-0 w-px h-full bg-[#D4AF37]/30 -translate-x-1/2" data-id="element-8375" data-mp="slamwh0n8kng" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. Quick Support Options */}
      <section className="py-24 px-4 bg-[#131B2B]/40 border-y border-[#D4AF37]/10" data-id="element-8376" data-mp="i107t3pplfag">
        <div className="max-w-6xl mx-auto" data-id="element-8377" data-mp="hyvxo417vk1q">
          <SectionHeader title="Choose How to Support" data-id="element-8378" data-mp="dz57a1flr5xq" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-8379" data-mp="e1refzykzjff">
            {[{
            title: 'Buy a Court Packet',
            price: 'From $14.99',
            icon: Package,
            copy: 'Open a premium digital packet and reveal portraits, seals, treasures, or memory fragments from the Lahore Treasury.',
            bullets: ['3 collectible reveals', 'Chance of Rare, Epic, or Treasury Rare', 'Adds to your personal collection', 'Includes lore and historical notes'],
            cta: 'Buy Court Packet',
            action: () => openSponsorModal('Court Packet')
          }, {
            title: 'Sponsor a Student',
            price: '$25',
            icon: GraduationCap,
            copy: 'Help a young person experience The Kingdoms of Punjab and receive their first Lahore Treasury collectible.',
            bullets: ['Supports student access', 'Includes one digital Court Packet', 'Optional patron wall recognition', 'Ideal for families and alumni'],
            cta: 'Sponsor a Student',
            action: () => openSponsorModal('Student Access')
          }, {
            title: 'Sponsor a School Bus',
            price: '$1,500–$2,500',
            icon: Bus,
            copy: 'Bring an entire group of students to the live experience and give them a cultural memory they can keep.',
            bullets: ['Supports up to 50 students', 'Includes group Court Packet unlock', 'Recognition as a Bus Patron', 'Optional school dedication'],
            cta: 'Sponsor a Bus',
            action: () => openSponsorModal('School Bus')
          }, {
            title: 'Buy Chaa for the Team',
            price: '$11 / $21 / $51',
            icon: Coffee,
            copy: 'Support the artists, guides, volunteers, and builders bringing the Lahore Treasury to life.',
            bullets: ['Small patron gesture', 'Helps support production days', 'Unlocks a thank-you badge', 'Optional message to the team'],
            cta: 'Buy Chaa',
            action: () => setIsChaaModalOpen(true)
          }].map((card, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="group relative bg-[#0B0F19] border border-[#D4AF37]/20 p-8 hover:border-[#D4AF37]/60 transition-all duration-300 flex flex-col" data-id="element-8380" data-mp="3sfqryodq91kg" data-mpi={i}>
                <div className="w-12 h-12 rounded-full bg-[#131B2B] border border-[#D4AF37]/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-8381" data-mp="2zqa3u2x546u1">
                  <card.icon className="w-5 h-5 text-[#D4AF37]" data-id="element-8382" data-mp="xzgjl2c7j1fp" />
                </div>
                <h3 className="font-serif text-xl text-[#FDFBF7] mb-1" data-id="element-8383" data-mp="xxcewi6hhyed">
                  {card.title}
                </h3>
                <p className="text-[#D4AF37] font-mono text-sm mb-4" data-id="element-8384" data-mp="2bvq7z0bdjs98">
                  {card.price}
                </p>
                <p className="text-[#FDFBF7]/60 text-sm mb-6 leading-relaxed" data-id="element-8385" data-mp="2gtrq9j0z7njn">
                  {card.copy}
                </p>
                <ul className="space-y-3 mb-8 flex-1" data-id="element-8386" data-mp="2lltaps8askkr">
                  {card.bullets.map((b, j) => <li key={j} className="flex items-start gap-2 text-xs text-[#FDFBF7]/50" data-id="element-8387" data-mp="2pm3fx2o9n6yk" data-mpi={j}>
                      <span className="w-1 h-1 rounded-full bg-[#D4AF37] mt-1.5 shrink-0" data-id="element-8388" data-mp="1xuxd2eh5uv5l" />
                      {b}
                    </li>)}
                </ul>
                <button onClick={card.action} className="w-full py-3 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#0B0F19] transition-colors" data-id="element-8389" data-mp="26w5c4yri400q">
                  {card.cta}
                </button>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 3. Impact Calculator */}
      <section className="py-24 px-4 relative" data-id="element-8390" data-mp="3bwlmbq307tas">
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-8391" data-mp="3pv35rblvmjmw">
          <div className="bg-[#131B2B] border border-[#D4AF37]/30 p-8 sm:p-12 relative overflow-hidden" data-id="element-8392" data-mp="3pwmod8v096ru">
            <CornerOrnaments data-id="element-8393" data-mp="3ls6iv8pb01d5" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-[radial-gradient(circle_at_top_right,_rgba(212,175,55,0.1),_transparent_70%)]" data-id="element-8394" data-mp="3nv7et9djd0d" />

            <div className="text-center mb-10" data-id="element-8395" data-mp="3ut4mhpvty6v7">
              <h2 className="font-serif text-3xl text-[#FDFBF7] mb-3" data-id="element-8396" data-mp="3usbnjb344lnm">
                Build Your Impact
              </h2>
              <p className="text-[#FDFBF7]/60 text-sm" data-id="element-8397" data-mp="8e1tomgeylfg">
                Choose a contribution and see what it helps unlock.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-3 mb-10" data-id="element-8398" data-mp="mc9xnebvan04">
              {[{
              label: '1 Student',
              val: 25
            }, {
              label: '10 Students',
              val: 250
            }, {
              label: '1 School Bus',
              val: 2000
            }, {
              label: 'Chaa',
              val: 21
            }].map(opt => <button key={opt.val} onClick={() => {
              setImpactAmount(opt.val);
              setCustomImpact('');
            }} className={`px-6 py-3 border text-xs tracking-wider uppercase transition-colors ${impactAmount === opt.val ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-[#D4AF37]/20 text-[#FDFBF7]/60 hover:border-[#D4AF37]/50'}`} data-id="element-8399" data-mp="ia1yz17lpnj0">
                  {opt.label} (${opt.val})
                </button>)}
              <div className="relative" data-id="element-8400" data-mp="4a99183wfbc3">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#FDFBF7]/40 text-sm" data-id="element-8401" data-mp="25zon728ydk8j">
                  $
                </span>
                <input type="number" placeholder="Custom" value={customImpact} onChange={e => {
                setCustomImpact(e.target.value);
                setImpactAmount(Number(e.target.value) || 0);
              }} className="w-32 bg-[#0B0F19] border border-[#D4AF37]/20 pl-8 pr-4 py-3 text-xs text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors uppercase tracking-wider" data-id="element-8402" data-mp="218gey9jd04wy" />
              </div>
            </div>

            <div className="text-center mb-10" data-id="element-8403" data-mp="2azkqt0dqn9nk">
              <p className="text-xl sm:text-2xl font-serif text-[#FDFBF7] leading-relaxed" data-id="element-8404" data-mp="2ayru6s8hu0j5">
                Your support helps bring{' '}
                <span className="text-[#D4AF37] text-3xl sm:text-4xl mx-2" data-id="element-8405" data-mp="zk5wk4cwyvw5">
                  {currentImpact.students}
                </span>{' '}
                students into the Lahore Court experience.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-8 border-t border-[#D4AF37]/15" data-id="element-8406" data-mp="3g746vlwz6ka2">
              <div className="text-center" data-id="element-8407" data-mp="3k74lp7nzkxhn">
                <p className="text-[#D4AF37] font-serif text-3xl mb-1" data-id="element-8408" data-mp="3oymlhse9ws11">
                  {currentImpact.students}
                </p>
                <p className="text-[#FDFBF7]/40 text-[10px] tracking-[0.2em] uppercase" data-id="element-8409" data-mp="3two286o88f9c">
                  Students Supported
                </p>
              </div>
              <div className="text-center" data-id="element-8410" data-mp="7qj1sg7gsbc8">
                <p className="text-[#D4AF37] font-serif text-3xl mb-1" data-id="element-8411" data-mp="7ru8ansanx71">
                  {currentImpact.packets}
                </p>
                <p className="text-[#FDFBF7]/40 text-[10px] tracking-[0.2em] uppercase" data-id="element-8412" data-mp="bq081buptcig">
                  Packets Unlocked
                </p>
              </div>
              <div className="text-center" data-id="element-8413" data-mp="2dk90cvawmqop">
                <p className="text-[#D4AF37] font-serif text-3xl mb-1" data-id="element-8414" data-mp="2dlambutgkxl7">
                  {currentImpact.buses > 0 ? currentImpact.buses : '< 1'}
                </p>
                <p className="text-[#FDFBF7]/40 text-[10px] tracking-[0.2em] uppercase" data-id="element-8415" data-mp="1pnmg1d3mwr1a">
                  Buses Funded
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Sponsor a Collection */}
      <section className="py-24 px-4 bg-[#131B2B]/40 border-y border-[#D4AF37]/10" data-id="element-8416" data-mp="1bnuv0sepaads">
        <div className="max-w-6xl mx-auto" data-id="element-8417" data-mp="16p9yb2ynq8u3">
          <SectionHeader title="Sponsor a Collection" subtitle="Help preserve and present one part of the Lahore Treasury." data-id="element-8418" data-mp="16ql8plmlyeu6" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-id="element-8419" data-mp="1fq7zzelmi49i">
            {[{
            name: 'Lahore Court Collection',
            desc: 'Founding faces of sovereignty.',
            price: '$5,000',
            sponsoredBy: 'Akal Steel',
            isFlagship: true
          }, {
            name: 'Founders’ Seals',
            desc: 'Authority made visible.',
            price: '$7,500'
          }, {
            name: 'Court Treasures',
            desc: 'Jewels, regalia, and royal objects.',
            price: '$10,000'
          }, {
            name: 'Royal Women & Chamber Culture',
            desc: 'The inner court of counsel, lineage, music, and refinement.',
            price: '$7,500'
          }, {
            name: 'Sacred Authority',
            desc: 'Reverence before rule.',
            price: '$10,000'
          }, {
            name: 'Frontier Defenders',
            desc: 'Martial prestige at the empire’s edge.',
            price: '$7,500'
          }].map((col: any, i: number) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className={`group relative bg-[#0B0F19] border p-8 transition-all duration-300 ${col.sponsoredBy ? 'border-[#D4AF37]/50 shadow-[0_0_30px_rgba(212,175,55,0.08)]' : 'border-[#D4AF37]/20 hover:border-[#D4AF37]/60'}`} data-id="element-8420" data-mp="9y4snivmqyzj" data-mpi={i}>
                {col.sponsoredBy && <span className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-8421" data-mp="12yip3vpe1jon" />}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.05),_transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-8422" data-mp="2ea9s5z88pn4i" />
                {col.isFlagship && <p className="text-[#D4AF37] text-[9px] tracking-[0.3em] uppercase font-bold mb-2 relative z-10" data-id="element-8423" data-mp="6orruob2zyua">
                    Flagship Collection
                  </p>}
                <h3 className="font-serif text-xl text-[#FDFBF7] mb-2 relative z-10" data-id="element-8424" data-mp="3u12k3vhikqyf">
                  {col.name}
                </h3>
                <p className="text-[#FDFBF7]/60 text-sm mb-6 h-10 relative z-10" data-id="element-8425" data-mp="3k4fyh4hd5vvc">
                  {col.desc}
                </p>
                {col.sponsoredBy ? <>
                    <div className="pt-6 border-t border-[#D4AF37]/30 mb-6 relative z-10" data-id="element-8426" data-mp="3f7pjkkhap6bi">
                      <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mb-1.5" data-id="element-8427" data-mp="3b5m7btmsxxp6">
                        Presented in partnership with
                      </p>
                      <p className="font-serif text-xl text-[#D4AF37] leading-none" data-id="element-8428" data-mp="19bmwtahi7kdj">
                        {col.sponsoredBy}
                      </p>
                      <p className="text-[#FDFBF7]/40 text-[10px] mt-2 italic" data-id="element-8429" data-mp="1nhes5gk586bt">
                        Sponsorship secured.
                      </p>
                    </div>
                    <button onClick={() => openSponsorModal('Collection')} className="w-full py-3 bg-[#D4AF37]/8 border border-[#D4AF37]/40 text-[#D4AF37]/85 text-[10px] tracking-[0.2em] uppercase hover:bg-[#D4AF37]/15 transition-colors relative z-10" data-id="element-8430" data-mp="1eh8kibb4m0kk">
                      Inquire About Other Tiers
                    </button>
                  </> : <>
                    <div className="pt-6 border-t border-[#D4AF37]/15 mb-6 relative z-10" data-id="element-8431" data-mp="1x782ke8jufeh">
                      <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mb-1" data-id="element-8432" data-mp="225jgp15vnc2i">
                        Starting From
                      </p>
                      <p className="text-[#D4AF37] font-mono text-lg" data-id="element-8433" data-mp="qs75u6bp9t6u">
                        {col.price}
                      </p>
                    </div>
                    <button onClick={() => openSponsorModal('Collection')} className="w-full py-3 bg-[#131B2B] border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#0B0F19] transition-colors relative z-10" data-id="element-8434" data-mp="ja9cbb9wysp9">
                      Become a Patron
                    </button>
                  </>}
              </motion.div>)}
          </div>
          <p className="text-center text-[#FDFBF7]/30 text-[10px] max-w-2xl mx-auto uppercase tracking-wider" data-id="element-8435" data-mp="3rpt19y3jxoyi">
            Collection sponsors may receive recognition on the website, event
            materials, patron wall, and selected digital collectible pages.
            Final recognition is subject to cultural and editorial approval.
          </p>
        </div>
      </section>

      {/* 5. Patron Tiers */}
      <section className="py-24 px-4" data-id="element-8436" data-mp="37xwc2excuefj">
        <div className="max-w-6xl mx-auto" data-id="element-8437" data-mp="3lnhaoz67byhv">
          <SectionHeader title="Patron Tiers" subtitle="For families, businesses, foundations, and community leaders who want to help build the Lahore Treasury." data-id="element-8438" data-mp="3loiy834emdgh" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-8439" data-mp="3vjkoj2af1lkn">
            {[{
            tier: 'Friend of the Court',
            price: '$101',
            benefits: ['Name on digital patron wall', '1 supporter badge', '1 Court Packet'],
            cta: 'Become a Friend',
            type: 'Other'
          }, {
            tier: 'Darbar Patron',
            price: '$501',
            benefits: ['Patron wall recognition', '5 Court Packets', 'Early access to selected drops', 'Invitation to patron preview'],
            cta: 'Become a Patron',
            type: 'Other'
          }, {
            tier: 'Treasury Patron',
            price: '$2,500',
            benefits: ['Sponsor student access', '15 Court Packets', 'Recognition on selected collection page', 'Optional family dedication'],
            cta: 'Become a Treasury Patron',
            type: 'Other'
          }, {
            tier: 'Founding Patron',
            price: '$10,000+',
            benefits: ['Major recognition', 'Collection sponsorship eligibility', 'Private preview experience', 'Founder’s Edition digital collectible', 'Optional commemorative coin eligibility'],
            cta: 'Apply as Founding Patron',
            type: 'Founding Partner',
            isPremium: true
          }].map((tier, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className={`relative p-8 flex flex-col ${tier.isPremium ? 'bg-gradient-to-b from-[#131B2B] to-[#0B0F19] border border-[#D4AF37] shadow-[0_0_30px_rgba(212,175,55,0.15)]' : 'bg-[#131B2B]/40 border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-colors'}`} data-id="element-8440" data-mp="61qw7eoso2yz" data-mpi={i}>
                {tier.isPremium && <CornerOrnaments data-id="element-8441" data-mp="2viya2a7ynfa0" />}
                <h3 className={`font-serif text-xl mb-2 ${tier.isPremium ? 'text-[#D4AF37]' : 'text-[#FDFBF7]'}`} data-id="element-8442" data-mp="30hjcmgaoqb1m">
                  {tier.tier}
                </h3>
                <p className="font-mono text-lg text-[#FDFBF7]/80 mb-6 pb-6 border-b border-[#D4AF37]/15" data-id="element-8443" data-mp="2bwo3faodxnft">
                  {tier.price}
                </p>
                <ul className="space-y-4 mb-8 flex-1" data-id="element-8444" data-mp="2qvi5b3geekr1">
                  {tier.benefits.map((b, j) => <li key={j} className="flex items-start gap-3 text-sm text-[#FDFBF7]/70" data-id="element-8445" data-mp="2ltizlmx6rbqx" data-mpi={j}>
                      <Shield className={`w-4 h-4 shrink-0 mt-0.5 ${tier.isPremium ? 'text-[#D4AF37]' : 'text-[#D4AF37]/50'}`} data-id="element-8446" data-mp="2uwae5vlcynms" />
                      <span className="leading-tight" data-id="element-8447" data-mp="34lbr1cwlwowj">{b}</span>
                    </li>)}
                </ul>
                <button onClick={() => openSponsorModal(tier.type)} className={`w-full py-3 text-[10px] tracking-[0.2em] uppercase transition-colors ${tier.isPremium ? 'bg-[#D4AF37] text-[#0B0F19] hover:bg-[#E5C158] font-bold' : 'border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0B0F19]'}`} data-id="element-8448" data-mp="3ikbjywr5jpyi">
                  {tier.cta}
                </button>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 6. Where Your Support Goes */}
      <section className="py-24 px-4 bg-[#131B2B]/40 border-y border-[#D4AF37]/10" data-id="element-8449" data-mp="12qqdcr42lv9n">
        <div className="max-w-5xl mx-auto" data-id="element-8450" data-mp="64erpcyv0kwt">
          <SectionHeader title="Where Your Support Goes" data-id="element-8451" data-mp="b1eu9dwi35j6" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-8452" data-mp="b1wt8iajrxus">
            {[{
            icon: Users,
            label: 'Student Access'
          }, {
            icon: Bus,
            label: 'School Buses'
          }, {
            icon: Camera,
            label: 'Artists & Designers'
          }, {
            icon: BookOpen,
            label: 'Historical Research'
          }, {
            icon: Globe,
            label: 'Live Experience Production'
          }, {
            icon: Package,
            label: 'Digital Collectible Platform'
          }, {
            icon: Heart,
            label: 'Community Programming'
          }, {
            icon: Shield,
            label: 'Preservation of Heritage'
          }].map((item, i) => <div key={i} className="flex flex-col items-center" data-id="element-8453" data-mp="2s7vhnsb09lym" data-mpi={i}>
                <div className="w-12 h-12 rounded-full border border-[#D4AF37]/20 bg-[#0B0F19] flex items-center justify-center mb-4" data-id="element-8454" data-mp="2nbojwmsrjgrr">
                  <item.icon className="w-5 h-5 text-[#D4AF37]/70" data-id="element-8455" data-mp="2j7gsuiyu1oqd" />
                </div>
                <p className="text-[#FDFBF7]/80 text-xs uppercase tracking-wider leading-relaxed max-w-[120px]" data-id="element-8456" data-mp="2ec9rhht1cw4m">
                  {item.label}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. Sponsor Opportunities */}
      <section className="py-24 px-4" data-id="element-8457" data-mp="1clz9oy4d429m">
        <div className="max-w-6xl mx-auto" data-id="element-8458" data-mp="17ogxmyv1t6xe">
          <div className="text-center mb-16" data-id="element-8459" data-mp="17p8s26cwsym3">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-8460" data-mp="12pe1y50ykd23">
              For Sponsors
            </h2>
            <p className="text-[#D4AF37] text-lg font-serif italic mb-4" data-id="element-8461" data-mp="20do4cl4np2tq">
              Sponsor a cultural moment, not just a logo placement.
            </p>
            <p className="text-[#FDFBF7]/60 text-sm max-w-2xl mx-auto" data-id="element-8462" data-mp="1vbypsvi8670w">
              Lahore Treasury gives sponsors the ability to support student
              access, live cultural programming, digital collectibles,
              commemorative rewards, and community recognition.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-8463" data-mp="a0ow5soyjjk0">
            {[{
            title: 'Student Access Sponsor',
            price: 'Custom',
            bestFor: 'Families, Foundations'
          }, {
            title: 'School Bus Sponsor',
            price: 'From $1,500',
            bestFor: 'Businesses, Community Groups'
          }, {
            title: 'Collection Sponsor',
            price: 'From $5,000',
            bestFor: 'Families, Businesses'
          }, {
            title: 'Treasury Night Sponsor',
            price: 'Custom',
            bestFor: 'Corporate Sponsors'
          }, {
            title: 'Commemorative Coin Sponsor',
            price: 'Custom',
            bestFor: 'Elected Officials, Businesses'
          }, {
            title: 'Founding Partner',
            price: '$10,000+',
            bestFor: 'Major Philanthropists'
          }].map((opp, i) => <div key={i} className="bg-[#131B2B]/60 border border-[#D4AF37]/20 p-6 flex flex-col hover:border-[#D4AF37]/50 transition-colors" data-id="element-8464" data-mp="1gwbvk427ngdx" data-mpi={i}>
                <h3 className="font-serif text-lg text-[#FDFBF7] mb-1" data-id="element-8465" data-mp="1qvji9huu4ndp">
                  {opp.title}
                </h3>
                <p className="text-[#D4AF37] font-mono text-sm mb-4" data-id="element-8466" data-mp="1lvf0loi6xmlw">
                  {opp.price}
                </p>
                <div className="mt-auto pt-4 border-t border-[#D4AF37]/10 mb-6" data-id="element-8467" data-mp="1zu4yv7gqxsbu">
                  <p className="text-[#FDFBF7]/40 text-[10px] tracking-widest uppercase mb-1" data-id="element-8468" data-mp="1vtkwnphbfyiu">
                    Best For
                  </p>
                  <p className="text-[#FDFBF7]/80 text-xs" data-id="element-8469" data-mp="24mo7dq0ktx9s">{opp.bestFor}</p>
                </div>
                <button onClick={() => openSponsorModal(opp.title)} className="w-full py-2.5 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#0B0F19] transition-colors" data-id="element-8470" data-mp="2jjyk12oyh973">
                  Request Sponsor Info
                </button>
              </div>)}
          </div>
        </div>
      </section>

      {/* 8. FAQ Section */}
      <section className="py-24 px-4 bg-[#131B2B]/40 border-y border-[#D4AF37]/10" data-id="element-8471" data-mp="2o7y7vthwmy5o">
        <div className="max-w-3xl mx-auto" data-id="element-8472" data-mp="2t6ht1rooc5wz">
          <SectionHeader title="Questions Before You Support?" data-id="element-8473" data-mp="2t9dw1ufdpksa" />
          <div className="space-y-4" data-id="element-8474" data-mp="2eh3lmp4nryk8">
            {[{
            q: 'Is this an NFT project?',
            a: 'No. Lahore Treasury uses digital collectibles for cultural storytelling, event access, recognition, and collection-building. It should not be presented as a crypto or investment product.'
          }, {
            q: 'Can I sponsor a specific school?',
            a: 'Yes, where operationally possible. Sponsors can request a school, region, or community group.'
          }, {
            q: 'Can my family or business be recognized?',
            a: 'Yes. Recognition options may include the patron wall, selected collection pages, event materials, or sponsor acknowledgements, subject to cultural and editorial approval.'
          }, {
            q: 'Can I make a custom contribution?',
            a: 'Yes. Custom support is available for patrons, sponsors, families, and foundations.'
          }, {
            q: 'What does "Buy Chaa for the Team" mean?',
            a: 'It is a small contribution that supports the artists, guides, volunteers, and builders working behind the scenes.'
          }].map((faq, i) => {
            const [isOpen, setIsOpen] = useState(false);
            return <div key={i} className="border border-[#D4AF37]/20 bg-[#0B0F19]" data-id="element-8475" data-mp="9e0mnnmud76e" data-mpi={i}>
                  <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-5 flex items-center justify-between text-left" data-id="element-8476" data-mp="4fh0bm4c5tdi">
                    <span className="font-serif text-lg text-[#FDFBF7] pr-4" data-id="element-8477" data-mp="gvgrc74aovq3">
                      {faq.q}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-[#D4AF37] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-8478" data-mp="qn380izejn08" />
                  </button>
                  <AnimatePresence data-id="element-8479" data-mp="3u3jspnkvwh2k">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden" data-id="element-8480" data-mp="3u5w9vyitinek">
                        <div className="px-6 pb-5 text-[#FDFBF7]/60 text-sm leading-relaxed border-t border-[#D4AF37]/10 pt-4" data-id="element-8481" data-mp="3ag2cohaqttv1">
                          {faq.a}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* 9. Final CTA */}
      <section className="py-32 px-4 relative overflow-hidden" data-id="element-8482" data-mp="2rpklih5k76bs">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(212,175,55,0.08),_transparent_60%)]" data-id="element-8483" data-mp="2rr5e418bcf70" />
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-8484" data-mp="30q0pdrq7sfh6">
          <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-6" data-id="element-8485" data-mp="30opl7qsux5pj">
            Help Open the Lahore Court
          </h2>
          <p className="text-[#FDFBF7]/60 text-lg mb-12" data-id="element-8486" data-mp="28z2wonnukuvp">
            Support a student, fund a bus, sponsor a collection, or become a
            patron of the Treasury.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-8487" data-mp="1yt1ma4o1zuu1">
            <button onClick={() => openSponsorModal('Student Access')} className="w-full sm:w-auto px-8 py-4 bg-[#D4AF37] text-[#0B0F19] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#E5C158] transition-colors" data-id="element-8488" data-mp="23npf2fq3bbef">
              Sponsor a Student
            </button>
            <button onClick={() => openSponsorModal('Founding Partner')} className="w-full sm:w-auto px-8 py-4 border border-[#D4AF37]/40 text-[#D4AF37] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#D4AF37]/10 transition-colors" data-id="element-8489" data-mp="2mkgsw9mv4yb3">
              Become a Patron
            </button>
            <button onClick={() => openSponsorModal('Other')} className="w-full sm:w-auto px-8 py-4 border border-[#FDFBF7]/20 text-[#FDFBF7] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#FDFBF7]/10 transition-colors" data-id="element-8490" data-mp="w7rn649ebeny">
              Request Sponsor Info
            </button>
          </div>
        </div>
      </section>

      {/* 10. Footer */}
      <footer className="px-4 py-16 bg-[#0B0F19] border-t border-[#D4AF37]/20 text-center" data-id="element-8491" data-mp="8hnepijdlshv">
        <div className="max-w-4xl mx-auto" data-id="element-8492" data-mp="4hbug6uxdu6x">
          <OrnateDivider className="mb-10" data-id="element-8493" data-mp="4imym7sa93yk" />

          <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 mb-12" data-id="element-8494" data-mp="116t755sbsgi4">
            <Link to="/treasure" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-8495" data-mp="w7zv7hsosxk2">
              Treasury Home
            </Link>
            <Link to="/treasure" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-8496" data-mp="ng7q7mmfmgmv">
              Collections
            </Link>
            <Link to="/treasure" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-8497" data-mp="1f62erh2gu2ah">
              Court Packet
            </Link>
            <Link to="/support-the-court" className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-8498" data-mp="32sym9j8k61rg">
              Support the Court
            </Link>
            <a href="#" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-8499" data-mp="3br0mhwkgwqdo">
              Contact
            </a>
          </div>

          <p className="text-[#D4AF37] font-serif text-xl tracking-[0.3em] uppercase mb-3" data-id="element-8500" data-mp="3qij604cjd31s">
            Lahore Treasury
          </p>
          <p className="text-[#FDFBF7]/40 text-[10px] tracking-[0.25em] uppercase font-light" data-id="element-8501" data-mp="3uil27xzi549o">
            Enter the Lahore Court
          </p>
        </div>
      </footer>

      {/* Modals Mounted at Root */}
      <BuyChaaModal isOpen={isChaaModalOpen} onClose={() => setIsChaaModalOpen(false)} data-id="element-8502" data-mp="8d67em2bg1qx" />
      <SponsorInquiryModal isOpen={isSponsorModalOpen} onClose={() => setIsSponsorModalOpen(false)} defaultType={sponsorModalType} data-id="element-8503" data-mp="18mhh7rahtgov" />
    </div>;
};